import { _isNotEmpty, _notNil } from '@/littledash';
import type { SendResultReport } from '@/model/Agent.model';
import { useMemo, type FC } from 'react';
import SendRuleId from './SendRuleId';
import { countOf, formatCount } from '@/components/Agents/SendValidator/utils/SendText';
import {
  hasUnsettledRules,
  missingInputsText,
  unfinishedReasonOf,
  unsettledRulesOf,
  type SendUnsettledRule,
} from '@/components/Agents/SendValidator/utils/SendRuleOutcomes';
import styles from './SendResults.module.scss';

interface SendNotEvaluatedProps {
  report: SendResultReport;
  testId?: string;
}

/**
 * A rule's sentence from the server. For a rule with no check yet, that sentence describes the check
 * still to be built, so it is framed as such and can never read as a check that ran.
 */
const RuleProse: FC<{ rule: SendUnsettledRule }> = ({ rule }) => {
  if (!_notNil(rule.prose)) {
    return null;
  }
  return rule.notBuilt ? (
    <p className="f6 dark-gray ma0 mt1" data-test-element="rule-prose" data-test-key="not-built">
      <span className="fw6">Not built yet: </span>when built, it will check as follows. {rule.prose}
    </p>
  ) : (
    <p className="f6 dark-gray ma0 mt1" data-test-element="rule-prose">
      {rule.prose}
    </p>
  );
};

/** What the upload lacked for the rule, as the server names it: the columns to add or datasets to upload. */
const MissingInputs: FC<{ rule: SendUnsettledRule }> = ({ rule }) => {
  const text = missingInputsText(rule);
  if (!_notNil(text)) {
    return null;
  }
  return (
    <p className="f6 near-black ma0 mt1" data-test-element="missing-inputs">
      {text}
    </p>
  );
};

/** How a rule that stopped before finishing is summed up: what it had decided, then why it stopped. */
type Unfinished = 'undecided' | 'decided';

const unfinishedLead = (rule: SendUnsettledRule, unfinished: Unfinished): string => {
  if (unfinished === 'undecided') {
    return 'Stopped before deciding. ';
  }
  return rule.foundProblems
    ? 'Found problems in what it read, then stopped. '
    : 'Passed on what it read, then stopped. ';
};

const RuleList: FC<{ rules: ReadonlyArray<SendUnsettledRule>; unfinished?: Unfinished }> = ({ rules, unfinished }) => (
  <ul className={styles.unsettledRules}>
    {rules.map((rule) => (
      <li
        key={rule.ruleId}
        className={styles.unsettledRule}
        data-test-element="unsettled-rule"
        data-test-key={rule.ruleId}
      >
        {/* Every row is a published register rule. */}
        <SendRuleId id={rule.ruleId} publisher="cdisc" />
        {_notNil(unfinished) && (
          <p className="f6 dark-gray ma0 mt1" data-test-element="unfinished-reason">
            {unfinishedLead(rule, unfinished)}
            {unfinishedReasonOf(rule)}
          </p>
        )}
        <MissingInputs rule={rule} />
        <RuleProse rule={rule} />
      </li>
    ))}
  </ul>
);

/**
 * Every published rule the run did not settle, so a short findings list never reads as a clean
 * package. The headline counts are the server's tally; beneath them each rule is listed under the
 * reason it was not checked, in plain words. A check that decided on part of what it read, then
 * stopped, is listed apart, since the tally counts it as passed or failed. A check that failed to run
 * is marked as a defect, apart from the rules that simply do not apply, which are shown but are not a
 * gap. Renders nothing when every rule was settled.
 */
const SendNotEvaluated: FC<SendNotEvaluatedProps> = ({ report, testId = 'invivo-send-validator__not-evaluated' }) => {
  const unsettled = useMemo(() => unsettledRulesOf(report), [report]);
  const { tally } = report;
  if (!hasUnsettledRules(unsettled)) {
    return null;
  }
  return (
    <div className="ui-card pa4" data-test-component="SendNotEvaluated" data-testid={testId}>
      <h3 className={`${styles.sectionTitle} mb1`} data-test-element="not-evaluated-headline">
        {countOf(tally.not_evaluated, 'rule', 'rules')} not evaluated, of {formatCount(tally.total)}
      </h3>
      <p className="f6 dark-gray ma0 mb3">
        None of these was decided against your data, so none of them passed. Each is listed under why.
      </p>

      {unsettled.notEvaluated.map((group) => (
        <details
          key={group.key}
          className={styles.unsettledGroup}
          data-test-element="not-evaluated-group"
          data-test-key={group.key}
        >
          <summary className={styles.unsettledSummary}>
            {group.title} <span className="dark-gray fw4">· {countOf(group.rules.length, 'rule', 'rules')}</span>
          </summary>
          <RuleList rules={group.rules} />
        </details>
      ))}

      {_isNotEmpty(unsettled.didNotFinish) && (
        <details className={styles.unsettledGroup} data-test-element="did-not-finish">
          <summary className={styles.unsettledSummary}>
            Started but stopped before deciding{' '}
            <span className="dark-gray fw4">· {countOf(unsettled.didNotFinish.length, 'rule', 'rules')}</span>
          </summary>
          <RuleList rules={unsettled.didNotFinish} unfinished="undecided" />
        </details>
      )}

      {_isNotEmpty(unsettled.evaluationErrors) && (
        <details
          open
          className={`${styles.unsettledGroup} ${styles.unsettledDefect}`}
          data-test-element="evaluation-errors"
        >
          <summary className={styles.unsettledSummary}>
            The check couldn&apos;t run: a fault in the validator, not a finding about your data{' '}
            <span className="dark-gray fw4">· {countOf(unsettled.evaluationErrors.length, 'rule', 'rules')}</span>
          </summary>
          <RuleList rules={unsettled.evaluationErrors} />
        </details>
      )}

      {_isNotEmpty(unsettled.decidedInPart) && (
        <div className={styles.decidedInPart} data-test-element="decided-in-part">
          <p className="f6 fw6 near-black ma0 mb1">
            {countOf(unsettled.decidedInPart.length, 'rule was', 'rules were')} decided on only part of what it read.
            The rule checks above count these as passed or failed, but they did not finish.
          </p>
          <RuleList rules={unsettled.decidedInPart} unfinished="decided" />
        </div>
      )}

      {_isNotEmpty(unsettled.notApplicable) && (
        <div className={styles.notApplicable} data-test-element="not-applicable">
          <p className="f6 fw6 dark-gray ma0 mb1">
            {countOf(tally.not_applicable, 'rule does', 'rules do')} not apply to this upload. This is not a gap.
          </p>
          {unsettled.notApplicable.map((group) => (
            <details
              key={group.key}
              className={styles.unsettledGroup}
              data-test-element="not-applicable-group"
              data-test-key={group.key}
            >
              <summary className={styles.unsettledSummary}>
                {group.title} <span className="dark-gray fw4">· {countOf(group.rules.length, 'rule', 'rules')}</span>
              </summary>
              <RuleList rules={group.rules} />
            </details>
          ))}
        </div>
      )}
    </div>
  );
};

export default SendNotEvaluated;
